import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { createDecision } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { authLogger } from '../../lib/auth/authLogger';

interface LocationState {
  decisionType?: string;
}

export default function DecisionForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = location.state as LocationState;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: state?.decisionType || '',
    reversibility: '',
    importance: '',
  });

  useEffect(() => {
    if (state?.decisionType) {
      setFormData(prev => ({
        ...prev,
        type: state.decisionType as string
      }));
    }
  }, [state?.decisionType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      authLogger.error('ERROR', 'No user found for decision creation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await createDecision({
        ...formData,
        user_id: user.id,
        status: 'in_progress'
      });

      if (error) {
        authLogger.error('ERROR', 'Failed to create decision', error);
        throw error;
      }

      if (data) {
        authLogger.info('DECISION', 'Decision created successfully', {
          decisionId: data.id,
          userId: user.id
        });
        navigate(`/decisions/${data.id}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create decision';
      setError(errorMessage);
      authLogger.error('ERROR', 'Decision creation error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <button
        type="button"
        onClick={handleBack}
        className="flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </button>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">New Decision</h2>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Decision Title
            </label>
            <input
              type="text"
              name="title"
              id="title"
              required
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              Decision Type
            </label>
            <select
              name="type"
              id="type"
              required
              value={formData.type}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select type</option>
              <option value="professional">Professional</option>
              <option value="financial">Financial</option>
              <option value="health">Health</option>
              <option value="career">Career</option>
              <option value="relationships">Relationships</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label htmlFor="reversibility" className="block text-sm font-medium text-gray-700">
              Reversibility
            </label>
            <select
              name="reversibility"
              id="reversibility"
              required
              value={formData.reversibility}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select reversibility</option>
              <option value="reversible">Reversible</option>
              <option value="partially">Partially Reversible</option>
              <option value="irreversible">Irreversible</option>
              <option value="unsure">Not Sure</option>
            </select>
          </div>

          <div>
            <label htmlFor="importance" className="block text-sm font-medium text-gray-700">
              Importance
            </label>
            <select
              name="importance"
              id="importance"
              required
              value={formData.importance}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">Select importance</option>
              <option value="low_priority_quick_assessment">Low Priority</option>
              <option value="moderate_priority_thorough_quick">Moderate Priority</option>
              <option value="high_priority_urgent">High Priority (Urgent)</option>
              <option value="critical_in_depth_analysis">Critical Priority</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Creating...' : 'Create Decision'}
          </button>
        </div>
      </div>
    </form>
  );
}